import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const RichTextContext = createContext();

export const RichTextProvider = ({ children }) => {
  const editorRef = useRef(null);
  const [activeBlock, setActiveBlock] = useState("p");
  const [currentFontSize, setCurrentFontSize] = useState(null);
  const [isTextSelected, setIsTextSelected] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const [isBuildTrue, setIsBuildTrue] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
  const [activeLink, setActiveLink] = useState(null);
  const [showLinkEditor, setShowLinkEditor] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [imageModel, setImageModel] = useState(false);

  // 🔹 Detect current block tag
  const detectBlock = useCallback(() => {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;

    let node = selection.anchorNode;
    if (!node) return;

    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentElement;
    }

    if (!editorRef.current?.contains(node)) return;

    const blockTags = ["P", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "PRE"];

    while (node && node !== editorRef.current) {
      if (blockTags.includes(node.tagName)) {
        setActiveBlock((prev) =>
          prev !== node.tagName.toLowerCase() ? node.tagName.toLowerCase() : prev,
        );
        return;
      }
      node = node.parentElement;
    }

    setActiveBlock("p");
  }, []);

  const detectFontSize = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    let node = range.startContainer;

    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentElement;
    }

    if (!editorRef.current || !editorRef.current.contains(node)) return;

    let current = node;
    let fontSizePx = null;

    while (current && current !== editorRef.current) {
      if (current.style && current.style.fontSize) {
        fontSizePx = parseFloat(window.getComputedStyle(current).fontSize);
        break;
      }
      current = current.parentElement;
    }

    if (!fontSizePx) {
      fontSizePx = parseFloat(window.getComputedStyle(node).fontSize);
    }

    setCurrentFontSize((prev) => (prev !== fontSizePx ? fontSizePx : prev));
  }, [editorRef]);

  const detectSelectionAvailable = useCallback(() => {
    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0) {
      setIsTextSelected(false);
      return;
    }

    const range = selection.getRangeAt(0);
    if (range.collapsed) {
      setIsTextSelected(false);
      return;
    }
    if (!editorRef.current?.contains(range.commonAncestorContainer)) {
      setIsTextSelected(false);
      return;
    }
    setIsTextSelected(true);
  }, []);

  const detectLinkPopup = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      setShowToolbar(false);
      return;
    }

    let node = selection.anchorNode;
    if (!node) return;

    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentElement;
    }

    if (!editorRef.current?.contains(node)) {
      setShowToolbar(false);
      return;
    }

    while (node && node !== editorRef.current) {
      if (node.tagName === "A") {
        const rect = node.getBoundingClientRect();
        const editorRect = editorRef.current.getBoundingClientRect();

        setToolbarPosition({
          top: rect.top - editorRect.top - 50,
          left: rect.left - editorRect.left + rect.width / 2,
        });

        setActiveLink(node);
        setShowToolbar(true);
        return;
      }

      node = node.parentElement;
    }

    setShowToolbar(false);
  }, []);

  const clearState = () => {
    setShowLinkEditor(false);
    setShowToolbar(false);
    setActiveLink(null);
    setLinkText("");
    setLinkUrl("");
    setToolbarPosition({ top: 0, left: 0 });
  };

  useEffect(() => {
    const handleSelectionChange = () => {
      requestAnimationFrame(() => {
        detectFontSize();
        detectBlock();
        detectSelectionAvailable();
        detectLinkPopup();
      });
    };

    document.addEventListener("selectionchange", handleSelectionChange);

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [detectFontSize, detectBlock, detectSelectionAvailable, detectLinkPopup]);

  return (
    <RichTextContext.Provider
      value={{
        editorRef,
        activeBlock,
        setActiveBlock,
        currentFontSize,
        setCurrentFontSize,
        isBuildTrue,
        setIsBuildTrue,
        loading,
        setLoading,
        status,
        setStatus,
        isTextSelected,
        setIsTextSelected,
        showToolbar,
        setShowToolbar,
        toolbarPosition,
        setToolbarPosition,
        activeLink,
        setActiveLink,
        showLinkEditor,
        setShowLinkEditor,
        linkText,
        setLinkText,
        linkUrl,
        setLinkUrl,
        imageModel,
        setImageModel,
        clearState,
      }}
    >
      {children}
    </RichTextContext.Provider>
  );
};

export const useRichText = () => useContext(RichTextContext);
